import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { PDFManagerFactory, WebViewerWrapper } from 'pdftron';
import store from 'store/store';
import { v4 as uuidv4 } from 'uuid';
import { DocumentTypes, PDFTronTools, DifferenceTypeNames, MarkupZoneCustomData, AnnotationCustomData } from 'types';
import {
  getSelectedTool,
  getShowDifferenceBoxes,
  getGraphicMode,
  getShowLiveText,
  inspection,
  getGrayscaleMode,
} from 'store';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import {
  createMarkupDifference,
  linkMarkupDifference,
  linkEmbeddedImageMatch,
  startThumbnailUploadMarkup,
} from 'store/request/differences/actions';
import { AnnotationData, Difference } from 'store/request/differences/types';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { getInspectionId, getIsSingleFile } from 'store/inspection/inspectionSelectors';
import { getDifferences } from 'store/request/differences/selectors';
import selectTool from 'utils/selectTool/selectTool';
import { trackEvent } from 'components/Tracker/TrackerProvider';
import { getTrackerMetadata } from 'store/trackerMetadata/trackerMetadata';
import { renderRectToOpaquePNG } from 'pdftron/utils/thumbnailUtils';
import { FlashAnimationManager, AnimationConfig } from './utils/FlashAnimationManager';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { CrosshairManager } from './utils/CrosshairManager';
import { getPageRotation, calculateRotatedLabelPosition, drawCounterRotatedLabel } from './utils/labelPositionUtils';
import { createAnnotationPopupButton } from './utils/createAnnotationPopupButton';

export interface MarkupZoneData {
  pageNumber: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  documentType: DocumentTypes;
}

export enum MarkupMode {
  NEW = 'new',
  COMPARISON = 'comparison',
  EMBEDDED_IMAGE_MATCH = 'embeddedImageMatch',
}

class MarkupZone {
  source: WebViewerWrapper | null = null;
  target: WebViewerWrapper | null = null;

  public markupMode: MarkupMode = MarkupMode.NEW;
  public originalMarkupId: string | null = null;
  public comparisonSourceDocType: DocumentTypes | null = null;

  static animatedMarkup: string | null = null;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  createMarkupZoneAnnotation(instance: WebViewerInstance, markupZoneInstance: MarkupZone) {
    // eslint-disable-next-line max-classes-per-file
    class MarkupZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        if (
          (markupZoneInstance.markupMode === MarkupMode.COMPARISON ||
            markupZoneInstance.markupMode === MarkupMode.EMBEDDED_IMAGE_MATCH) &&
          markupZoneInstance.originalMarkupId
        ) {
          this.Id = markupZoneInstance.originalMarkupId;
        } else {
          this.Id = uuidv4();
        }
        this.ToolName = PDFTronTools.MARKUP;
        this.IsHoverable = true;
        this.ReadOnly = true;
        this.disableRotationControl();
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);
        const showDifferenceBoxes = getShowDifferenceBoxes(store.getState());

        // Draw cyan selection rectangle
        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);

        if (this.getCustomData(MarkupZoneCustomData.markupZoneAnnotation)) {
          ctx.strokeStyle = showDifferenceBoxes ? '#cd3434' : 'rgba(255, 255, 255, 0.01)'; // transparent but clickable
        } else {
          ctx.strokeStyle = 'rgba(0, 255, 255, 1)'; // Cyan border (solid)
          ctx.fillStyle = 'rgba(0, 0, 0, 0.01)'; // transparent
        }

        ctx.fill(annotationRectangle);
        ctx.stroke(annotationRectangle);
      }
    }
    return MarkupZoneAnnotation;
  }

  createMarkupZoneAnnotationTools() {
    [
      { wrapper: this.source, documentType: DocumentTypes.source },
      { wrapper: this.target, documentType: DocumentTypes.target },
    ].forEach(({ wrapper, documentType }) => {
      if (!wrapper) return;
      const { instance } = wrapper;

      // eslint-disable-next-line max-classes-per-file
      const toolObject = new (class MarkupZoneCreateTool extends instance.Core.Tools.RectangleCreateTool {
        markupZoneInstance: MarkupZone;
        documentType: DocumentTypes;
        crosshairManager: CrosshairManager | null = null;

        constructor(markupZoneAnnotation: any, markupZoneInstance: MarkupZone, docType: DocumentTypes) {
          super(instance.Core.documentViewer);
          instance.Core.Tools.GenericAnnotationCreateTool.call(
            this,
            instance.Core.documentViewer,
            markupZoneAnnotation,
          );
          this.markupZoneInstance = markupZoneInstance;
          this.documentType = docType;
        }

        // switchIn is called when this tool is selected
        switchIn(previousTool: any) {
          super.switchIn(previousTool);
          // Set crosshair cursor
          this.cursor = 'crosshair';

          // Enable manual-tool-mode for consistent cursor and to disable annotation hover
          const viewerElement = instance.Core.documentViewer.getScrollViewElement();
          if (viewerElement) {
            viewerElement.classList.add('manual-tool-mode');
          }

          // Setup crosshairs
          this.crosshairManager = new CrosshairManager(instance, PDFTronTools.MARKUP);
          this.crosshairManager.setup();
        }

        // switchOut is called when switching away from this tool
        switchOut(newTool: any) {
          super.switchOut(newTool);
          const viewerElement = instance.Core.documentViewer.getScrollViewElement();
          if (viewerElement) {
            viewerElement.classList.remove('manual-tool-mode');
          }

          // Cleanup crosshairs
          if (this.crosshairManager) {
            this.crosshairManager.cleanup();
            this.crosshairManager = null;
          }
        }

        mouseLeftDown(e: any) {
          const state = store.getState();
          const selectedTool = getSelectedTool(state);
          const isComparisonMode = this.markupZoneInstance.markupMode === MarkupMode.COMPARISON;
          const isEmbeddedImageMatchMode = this.markupZoneInstance.markupMode === MarkupMode.EMBEDDED_IMAGE_MATCH;

          if (isComparisonMode) {
            const comparisonSourceDocType = this.markupZoneInstance.comparisonSourceDocType;
            const isOppositeDocument = comparisonSourceDocType && comparisonSourceDocType !== this.documentType;

            if (!isOppositeDocument) {
              return;
            }
          } else if (isEmbeddedImageMatchMode) {
            const drawingDocType = this.markupZoneInstance.comparisonSourceDocType;
            if (drawingDocType !== this.documentType) {
              return;
            }
          } else {
            if (selectedTool !== PDFTronTools.MARKUP) {
              return;
            }
          }

          super.mouseLeftDown(e);
        }
      })(this.createMarkupZoneAnnotation(instance, this), this, documentType);

      wrapper.registerTool({
        toolName: PDFTronTools.MARKUP,
        toolObject,
        buttonImage:
          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE2LjkyMjkgMTBIMTUuOTIyOVY3SDdWMTUuOTIyOUgxMC4xMjExVjE2LjkyMjlINlY2SDE2LjkyMjlWMTBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTE4IDE1LjQyODZIMTUuNDI4NlYxOEgxNC41NzE0VjE1LjQyODZIMTJWMTQuNTcxNEgxNC41NzE0VjEySDE1LjQyODZWMTQuNTcxNEgxOFYxNS40Mjg2WiIgZmlsbD0id2hpdGUiIHN0cm9rZT0id2hpdGUiLz4KPC9zdmc+',
      });
    });
  }

  private generateMarkupDifference(
    annotation: Core.Annotations.Annotation,
    annotationData: { source?: AnnotationData; target?: AnnotationData },
  ): Difference {
    const nextIndex = getDifferences(store.getState()).length + 1;

    const markup: Difference = {
      id: annotation.Id,
      type: DifferenceTypeNames.Markup,
      displayName: DifferenceTypeNames.Markup,
      comment: '',
      viewed: true,
      excluded: false,
      smartDiscard: false,
      groupId: '',
      source: {
        location: {
          // when a new markup is created, it will be created with 1 annotation. but a difference requires pair of annots
          pageNumber: 0,
          rect: [0, 0, 0, 0],
        },
        text: '',
      },
      target: {
        location: {
          pageNumber: 0,
          rect: [0, 0, 0, 0],
        },
        text: '',
      },
      description: 'markup',
      updatedAt: new Date().toISOString(),
      fontMetricsDetails: {
        fontPointSize: 0,
        contextTag: 'markup',
        fontSizeCheckValue: 0,
      },
      differenceIndex: nextIndex,
    };

    if (annotationData.source?.location) {
      markup.source.location = {
        pageNumber: annotationData.source.location.pageNumber || annotation.PageNumber,
        rect: annotationData.source.location.rect || [0, 0, 0, 0],
      };
    }

    if (annotationData.target?.location) {
      markup.target.location = {
        pageNumber: annotationData.target.location.pageNumber || annotation.PageNumber,
        rect: annotationData.target.location.rect || [0, 0, 0, 0],
      };
    }

    return markup;
  }

  // called from docManager/EventHandlers
  markupZoneAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    if (action === AnnotationAction.ADD) {
      // Check if this is a reload (ADD action with drawMarkups = 'true') vs user-initiated change
      const isReload = annotation.getCustomData(AnnotationCustomData.drawMarkups) === 'true';

      if (!isReload) {
        const currentInstance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
        // Only process user-initiated changes, not reloads
        if (this.markupMode === MarkupMode.COMPARISON && this.originalMarkupId) {
          this.createComparisonMarkup(documentType, annotation, this.originalMarkupId);
        } else if (this.markupMode === MarkupMode.EMBEDDED_IMAGE_MATCH && this.originalMarkupId) {
          this.linkEmbeddedImageMatch(documentType, annotation, this.originalMarkupId);
          return;
        } else {
          this.createStandaloneMarkup(documentType, annotation);
          const isSingleFile = getIsSingleFile(store.getState());
          if (!isSingleFile) {
            const comparisonPopup = this.getComparisonButtonPopup(documentType, annotation);
            currentInstance?.UI.annotationPopup.update([comparisonPopup]);
          }
        }
        setTimeout(() => {
          const pdfDocManager = PDFManagerFactory.getPDFDocManager();
          if (pdfDocManager) {
            currentInstance?.Core?.annotationManager?.redrawAnnotation(annotation);
            store.dispatch(inspection.actions.focusDifference(annotation.Id));
          }
        }, 500);

        this.resetMarkupMode(false);
      }
    }
  }

  private createStandaloneMarkup(documentType: DocumentTypes, annotation: Core.Annotations.Annotation) {
    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (!instance) return;

    const annotManager = PDFAnnotationManager.getInstance(documentType);

    const differenceDetails = this.generateMarkupDifference(annotation, {
      [documentType]: {
        location: {
          pageNumber: annotation.PageNumber,
          rect: [annotation.X, annotation.Y, annotation.Width, annotation.Height],
        },
      },
    });

    const state = store.getState();
    const inspectionId = getInspectionId(state);

    const pageNumber = annotation.PageNumber;

    const doc = instance.Core.documentViewer.getDocument();

    // Create and dispatch the markup difference BEFORE async thumbnail generation
    // This ensures the markup is added to Redux immediately (optimistic update)
    const backendDocumentType = documentType === DocumentTypes.source ? 'master' : 'sample';
    const currentDocumentData = differenceDetails[documentType];

    const backendDifferenceDetails = {
      differenceType: 'Markup',
      index: differenceDetails.differenceIndex,
      differenceIndex: differenceDetails.differenceIndex,
      [backendDocumentType]: {
        location: {
          pageNumber: currentDocumentData.location.pageNumber,
          boundingBox: {
            bottom: annotation.Y + annotation.Height,
            left: annotation.X,
            right: annotation.X + annotation.Width,
            top: annotation.Y,
          },
        },
      },
    };

    // Set custom data on annotation BEFORE dispatching to Redux
    // This ensures the annotation is properly identified as a markup when overlay is created
    annotation.setCustomData(AnnotationCustomData.difference, 'true');
    annotation.setCustomData(MarkupZoneCustomData.markupZoneAnnotation, 'true');

    store.dispatch(
      createMarkupDifference(inspectionId, differenceDetails.id, backendDifferenceDetails, differenceDetails),
    );

    const currentState = store.getState();
    const trackerMetadata = getTrackerMetadata(currentState);
    trackEvent({
      name: 'markup-created',
      data: {
        userEmail: trackerMetadata.userEmail,
        inspectionId,
        markupCoordinates: {
          pageNumber: annotation.PageNumber,
          x: annotation.X,
          y: annotation.Y,
          width: annotation.Width,
          height: annotation.Height,
        },
      },
    });

    // Generate thumbnail asynchronously - this doesn't need to block the UI
    renderRectToOpaquePNG(doc, pageNumber, annotation.getRect(), instance, 2)
      .then((blob) => {
        store.dispatch(startThumbnailUploadMarkup(inspectionId, differenceDetails.id, blob, documentType));
      })
      .catch(() => {
        // swallow; thumbnail failure should not block UI state restoration
      });
    setTimeout(() => {
      annotManager?.selectAnnotation(annotation);
      annotManager?.redrawAnnotation(annotation);
    }, 500);
  }

  private createComparisonMarkup(
    documentType: DocumentTypes,
    annotation: Core.Annotations.Annotation,
    originalId: string,
  ) {
    annotation.setCustomData('linkedMarkupId', originalId);
    annotation.setCustomData(AnnotationCustomData.difference, 'true');
    annotation.setCustomData(MarkupZoneCustomData.markupZoneAnnotation, 'true');
    const originalAnnotation = this.getOriginalMarkupAnnotation(originalId);
    if (originalAnnotation) {
      originalAnnotation.setCustomData('linkedMarkupId', annotation.Id);
    }

    const state = store.getState();
    const inspectionId = getInspectionId(state);

    const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
    if (!instance) return;

    const doc = instance.Core.documentViewer.getDocument();

    renderRectToOpaquePNG(doc, annotation.PageNumber, annotation.getRect(), instance, 2)
      .then((blob) => {
        try {
          // Link the markup coordinates to the opposite document
          const documentData = {
            location: {
              boundingBox: {
                bottom: annotation.Y + annotation.Height,
                left: annotation.X,
                right: annotation.X + annotation.Width,
                top: annotation.Y,
              },
              pageNumber: annotation.PageNumber,
            },
          };

          // Validate data before dispatching
          if (!originalId || !documentData.location.boundingBox || !documentData.location.pageNumber) {
            console.error('Invalid markup link data:', { originalId, documentData });
            return;
          }

          store.dispatch(linkMarkupDifference(originalId, documentType, documentData));

          // Upload thumbnail for the comparison side
          if (blob) {
            store.dispatch(startThumbnailUploadMarkup(inspectionId, originalId, blob, documentType));
          }

          // Track markup linking event
          const currentState = store.getState();
          const trackerMetadata = getTrackerMetadata(currentState);
          const originalAnnotation = this.getOriginalMarkupAnnotation(originalId);

          trackEvent({
            name: 'markup-linked',
            data: {
              userEmail: trackerMetadata.userEmail,
              inspectionId,
              existingMarkupCoordinates: originalAnnotation
                ? {
                    pageNumber: originalAnnotation.PageNumber,
                    x: originalAnnotation.X,
                    y: originalAnnotation.Y,
                    width: originalAnnotation.Width,
                    height: originalAnnotation.Height,
                  }
                : null,
              linkedMarkupCoordinates: {
                pageNumber: annotation.PageNumber,
                x: annotation.X,
                y: annotation.Y,
                width: annotation.Width,
                height: annotation.Height,
              },
            },
          });
        } catch (error) {
          console.error('Error linking markup:', error);
        }
      })
      .catch(() => {
        // swallow; thumbnail failure should not block UI state restoration
      });
  }

  private linkEmbeddedImageMatch(
    documentType: DocumentTypes,
    annotation: Core.Annotations.Annotation,
    differenceId: string,
  ) {
    annotation.setCustomData(AnnotationCustomData.difference, 'true');
    annotation.setCustomData(MarkupZoneCustomData.markupZoneAnnotation, 'true');

    const documentData = {
      location: {
        boundingBox: {
          bottom: annotation.Y + annotation.Height,
          left: annotation.X,
          right: annotation.X + annotation.Width,
          top: annotation.Y,
        },
        pageNumber: annotation.PageNumber,
      },
    };

    store.dispatch(linkEmbeddedImageMatch(differenceId, documentType, documentData));

    const currentInstance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;

    const inspectionId = getInspectionId(store.getState());
    if (currentInstance) {
      const doc = currentInstance.Core.documentViewer.getDocument();
      renderRectToOpaquePNG(doc, annotation.PageNumber, annotation.getRect(), currentInstance, 2)
        .then((blob) => {
          if (blob) {
            store.dispatch(startThumbnailUploadMarkup(inspectionId, differenceId, blob, documentType));
          }
        })
        .catch(() => {});
    }

    queueMicrotask(() => {
      // Targeted update: only sync the single changed difference on both sides
      // instead of iterating all differences via drawAllTexts.
      // The success saga's drawAllTexts reconciles everything when the server responds.
      const differences = getDifferences(store.getState());
      const diff = differences.find((d: Difference) => d.id === differenceId);
      if (diff) {
        PDFAnnotationManager.updateSingleDifference(diff, DocumentTypes.source);
        PDFAnnotationManager.updateSingleDifference(diff, DocumentTypes.target);
      }
      store.dispatch(inspection.actions.focusDifference(differenceId));
    });

    this.resetMarkupMode(false);
  }

  private getOriginalMarkupAnnotation(originalId: string): Core.Annotations.Annotation | null {
    // Check both source and target for the original annotation
    const sourceAnnotations = this.source?.instance.Core.annotationManager.getAnnotationsList() || [];
    const targetAnnotations = this.target?.instance.Core.annotationManager.getAnnotationsList() || [];
    const allAnnotations = [...sourceAnnotations, ...targetAnnotations];
    return allAnnotations.find((annotation) => annotation.Id === originalId) || null;
  }

  public resetMarkupMode(resetPopups: boolean = true) {
    this.markupMode = MarkupMode.NEW;
    this.originalMarkupId = null;
    this.comparisonSourceDocType = null;

    // Reset animation
    FlashAnimationManager.stopAllAnimations(this.source, this.target);

    if (resetPopups) {
      // Clear any annotation popups (comparison buttons) that might be showing
      this.source?.instance.UI.annotationPopup.update([]);
      this.target?.instance.UI.annotationPopup.update([]);
    }

    selectTool(PDFTronTools.PAN);

    this.source?.removeAnnotationDocumentHighlight();
    this.target?.removeAnnotationDocumentHighlight();
  }

  resetMarkupAnimation() {
    FlashAnimationManager.stopAnimation(
      'markupAnimationInterval',
      'MarkupAnnotationAnimated',
      this.source,
      this.target,
    );
    MarkupZone.animatedMarkup = null;
  }

  async animateMarkup(difference: Difference) {
    if (!this.target || !this.source) return;

    // Check if markup has both source and target thumbnails (linked)
    const sourceThumbnail = difference.thumbnailPaths?.source?.thumbnailUrl;
    const targetThumbnail = difference.thumbnailPaths?.target?.thumbnailUrl;

    if (!sourceThumbnail || !targetThumbnail) return;

    const animationId = difference.id;
    MarkupZone.animatedMarkup = animationId;
    this.source.annotationManager.deselectAllAnnotations();
    this.target.annotationManager.deselectAllAnnotations();

    const state = store.getState();
    const graphicMode = getGraphicMode(state);
    if (!graphicMode) return;

    try {
      // Fetch thumbnail blobs
      const [sourceBlob, targetBlob] = await Promise.all([
        FlashAnimationManager.fetchImageBlob(sourceThumbnail),
        FlashAnimationManager.fetchImageBlob(targetThumbnail),
      ]);

      // Check if animation was cancelled or switched during fetch
      if (MarkupZone.animatedMarkup !== animationId) {
        return;
      }

      // Create image bitmaps from thumbnails (no cropping needed)
      const [sourceImageBitmap, targetImageBitmap] = await Promise.all([
        createImageBitmap(sourceBlob),
        createImageBitmap(targetBlob),
      ]);

      // Final check before starting animation - markup might have changed during image processing
      if (MarkupZone.animatedMarkup !== animationId) {
        return;
      }

      // Use target location for positioning
      const targetLocation = difference.target.location;

      const config: AnimationConfig = {
        pageNumber: targetLocation.pageNumber,
        x: targetLocation.rect[0],
        y: targetLocation.rect[1],
        width: targetLocation.rect[2],
        height: targetLocation.rect[3],
        sourceImageBitmap,
        targetImageBitmap,
        animationId: difference.id,
        intervalName: 'markupAnimationInterval',
        annotationSubject: 'MarkupAnnotationAnimated',
        drawFn: (ctx, imageBitmap, details, isSource, alpha = 1) => {
          // Pass page number for dynamic rotation lookup at draw time
          this.drawMarkupFn(ctx, imageBitmap, { ...details, pageNumber: targetLocation.pageNumber }, isSource, alpha);
        },
        graphicMode,
      };

      await FlashAnimationManager.startFlashAnimation(this.target, config, () => MarkupZone.animatedMarkup);

      // Redraw live text if enabled
      const showLiveText = getShowLiveText(store.getState());
      if (showLiveText) {
        PDFTronManager.redrawLiveTextHighlights();
      }
    } catch (error) {
      console.error('Failed to animate markup:', error);
    }
  }

  private drawMarkupFn(
    ctx: CanvasRenderingContext2D,
    imageBitmap: ImageBitmap,
    {
      x = 0,
      y = 0,
      width = 0,
      height = 0,
      pageNumber = 1,
    }: { x: number; y: number; width: number; height: number; pageNumber?: number },
    isSource = false,
    imageAlpha = 1,
  ): void {
    ctx.save();

    // Apply alpha for the image content
    ctx.globalAlpha = imageAlpha;
    const state = store.getState();
    const grayscaleMode = getGrayscaleMode(state);
    if (grayscaleMode) {
      ctx.filter = 'grayscale(100%)';
    }
    ctx.drawImage(imageBitmap, x, y, width, height);
    if (grayscaleMode) {
      ctx.filter = 'none';
    }

    ctx.restore();

    // Get current page rotation dynamically at draw time so it updates when user rotates
    const pageRotation = this.target?.instance ? getPageRotation(this.target.instance, pageNumber || 1) : 0;

    // Draw border and label with fresh canvas state
    ctx.save();
    const showDifferenceBoxes = getShowDifferenceBoxes(state);

    const strokeStyle = showDifferenceBoxes ? '#6DDAE2' : 'rgba(255, 255, 255, 0.01)';
    const fillStyle = '#6DDAE2';
    const label = isSource ? 'Source' : 'New';

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x, y, width, height);

    // Draw label
    const labelFont = '11px Inter, sans-serif';
    ctx.font = labelFont;
    const textPadding = 4;
    const { width: textWidth } = ctx.measureText(label);
    // Labels render at their natural per-text width. The Source/New tail-overlap problem is
    // handled at the FlashAnimationManager layer by giving the animation annotations a
    // bounding rect that includes the label area, so PDFTron's per-annotation invalidation
    // clears the label pixels between frames. See FlashAnimationManager.startFlashAnimation.
    const labelWidth = textWidth + textPadding * 2;
    const labelHeight = 16;
    const labelBorderRadius = 2;

    // Calculate label position based on page rotation to keep it in perceived top-left
    const bounds = {
      x: Math.min(x, x + width), // handle negative width
      y: Math.min(y, y + height), // handle negative height
      width: Math.abs(width),
      height: Math.abs(height),
    };

    const labelDimensions = { width: labelWidth, height: labelHeight };
    const pgW = this.target?.instance
      ? this.target.instance.Core.documentViewer.getPageWidth(pageNumber || 1)
      : undefined;
    const pgH = this.target?.instance
      ? this.target.instance.Core.documentViewer.getPageHeight(pageNumber || 1)
      : undefined;
    const labelPosition = calculateRotatedLabelPosition(bounds, labelDimensions, pageRotation, 0, false, pgW, pgH);

    // Draw the complete label with counter-rotation so it always appears horizontal
    drawCounterRotatedLabel(
      ctx,
      label,
      labelPosition.x,
      labelPosition.y,
      labelWidth,
      labelHeight,
      pageRotation,
      fillStyle,
      textPadding,
      labelBorderRadius,
      undefined, // no icon
      12, // iconSize
      2, // iconPadding
      labelFont, // pass font explicitly
    );

    ctx.restore();
  }

  getComparisonButtonPopup(documentType: DocumentTypes, annotation: Core.Annotations.Annotation) {
    return createAnnotationPopupButton({
      label: 'Draw comparison',
      iconSrc: '/icons/drawComparison.svg',
      onClick: () => {
        this.markupMode = MarkupMode.COMPARISON;
        this.originalMarkupId = annotation.Id;
        this.comparisonSourceDocType = documentType;

        this[documentType === DocumentTypes.source ? 'target' : 'source']?.addDocumentHighlight();

        const oppositeDocType = documentType === DocumentTypes.source ? DocumentTypes.target : DocumentTypes.source;
        const oppositeInstance =
          oppositeDocType === DocumentTypes.source ? this.source?.instance : this.target?.instance;

        store.dispatch(inspection.actions.setSelectedTool({ tool: PDFTronTools.MARKUP }));
        if (oppositeInstance) {
          oppositeInstance.UI.setToolMode(PDFTronTools.MARKUP);
        }

        const sourceAnnotManager = PDFAnnotationManager.getInstance(DocumentTypes.source);
        const targetAnnotManager = PDFAnnotationManager.getInstance(DocumentTypes.target);

        sourceAnnotManager?.deselectAllAnnotations();
        targetAnnotManager?.deselectAllAnnotations();
      },
    });
  }

  // called from docManager/EventHandlers
  markupZoneAnnotationSelected(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    const isSingleFile = getIsSingleFile(store.getState());

    // selection logic for comparison mode
    if (!isSingleFile) {
      const oppositeWebviewer = documentType === DocumentTypes.source ? this.target : this.source;
      const matchingAnnotation = oppositeWebviewer?.getAnnotationById(annotation.Id);

      const currentInstance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
      if (this.markupMode !== MarkupMode.COMPARISON && !matchingAnnotation) {
        const comparisonPopup = this.getComparisonButtonPopup(documentType, annotation);
        currentInstance?.UI.annotationPopup.update([comparisonPopup]);
      } else {
        currentInstance?.UI.annotationPopup.update([]);
      }
    }
  }
}

export default MarkupZone;
